import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../Interface';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import ServiceOperationFields from './ServiceOperationFields';
import ServiceOperationReports from './ServiceOperationReports';
import useConfig from '../../context/ConfigContext';
import masterDataService from '../../service/masterData.service';
import serviceOperationService from '../../service/serviceOperation.service';
import ServiceOperationTabs from './ServiceOperationTabs';
import { useLoader } from '../../provider/LoaderProvider';

export const ServiceOperationPage = () => {
  const { loading, showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const { getConfig, config } = useConfig();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    quantityUnit: [],
    items: [],
  });
  const [autocompleteData, setAutoCompleteData] = useState({});
  const [serviceGroupLink, setServiceGroupLink] = useState([]);

  const defaultMaterial = {
    id: null,
    isNew: true,
    itemCode: '',
    qty: null,
    note: '',
    isRequired: false,
  };

  const defaultCheckList = {
    id: null,
    isNew: true,
    name: '',
    note: '',
    attachRequired: false,
    descriptionRequired: false,
  };

  const def = {
    code: '',
    name: '',
    standardTime: '',
    quantityUnitId: null,
    note: '',
    attachRequired: false,
    descriptionRequired: false,
    active: true,
    serviceMaterial: [],
    serviceCheckList: [],
  };

  useEffect(() => {
    getMenus();
    getConfig();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
    getServiceGroupLinks();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('serviceOperation');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (!loading && Object.keys(config).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          quantityUnitId: config?.defServiceOperationQtyUnit ?? null,
        };
      });
    }
  }, [config, loading]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    code: Yup.string().nullable(),
    name: Yup.string().required(),
    standardTime: Yup.number().required().positive(),
    quantityUnitId: Yup.string().required(),
    note: Yup.string().nullable(),
    attachRequired: Yup.boolean(),
    descriptionRequired: Yup.boolean(),
    active: Yup.boolean(),
    serviceMaterial: Yup.array().of(
      Yup.object().shape({
        itemCode: Yup.string().required(),
        qty: Yup.number().required(),
        note: Yup.string().nullable(),
        isRequired: Yup.boolean(),
      })
    ),
    serviceCheckList: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
        note: Yup.string().nullable(),
        attachRequired: Yup.boolean(),
        descriptionRequired: Yup.boolean(),
      })
    ),
    serviceOperationGroupLink: Yup.array().of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        checked: Yup.boolean().required(),
      })
    ),
  });

  const getServiceGroupLinks = () => {
    masterDataService.serviceGroupForm().then((data) => {
      const preparedData = data.map((group) => ({
        ...group,
        checked: false,
      }));

      setServiceGroupLink(preparedData);
      setInitValues((prev) => ({ ...prev, serviceOperationGroupLink: preparedData }));
      setDefaultValues((prev) => ({ ...prev, serviceOperationGroupLink: preparedData }));
    });
  };

  const initDefaultValues = () => {
    showLoader();
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemsFromTableToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          quantityUnit: resp[0],
          items: resp[1],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          quantityUnit: generateAutocompleteItems(aData.quantityUnit, 'code', 'id'),
          items: generateAutocompleteItems(aData.items, ['itemCode', 'itemName'], 'itemCode'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const valuesToForm = () => {
    const {
      id,
      code,
      name,
      standardTime,
      quantityUnitId,
      note,
      attachRequired,
      descriptionRequired,
      active,
      serviceMaterial,
      serviceCheckList,
      serviceOperationGroupLink: serviceOperationGroupLinkValues,
    } = selectedValues;

    const updatedServiceOperationGroupLink = serviceGroupLink?.map((serviceOperationGroup) => ({
      ...serviceOperationGroup,
      checked:
        serviceOperationGroupLinkValues?.some((form) => form.serviceGroupId === serviceOperationGroup.id) || false,
    }));

    setInitValues({
      id,
      code,
      name,
      standardTime,
      quantityUnitId,
      note,
      attachRequired,
      descriptionRequired,
      active,
      serviceCheckList: serviceCheckList.map((sc) => ({
        id: sc.id,
        name: sc.name,
        note: sc.note,
        attachRequired: sc.attachRequired,
        descriptionRequired: sc.descriptionRequired,
        createdBy: sc.createdBy,
      })),
      serviceMaterial: serviceMaterial.map((sm) => ({
        id: sm.id,
        itemCode: sm.itemCode,
        qty: sm.qty,
        note: sm.note,
        isRequired: sm.isRequired,
        createdBy: sm.createdBy,
      })),
      serviceOperationGroupLink: updatedServiceOperationGroupLink,
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
      serviceOperationGroupLink: values.serviceOperationGroupLink.filter((link) => link.checked).map((link) => link.id),
    };

    serviceOperationService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
      serviceOperationGroupLink: values.serviceOperationGroupLink.filter((link) => link.checked).map((link) => link.id),
    };

    serviceOperationService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    serviceOperationService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="ServiceOperation"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <div>
                <FormHeaderUI
                  addClick={() => {
                    setSelectedValues(defaultSelectedValues);
                    setViewField(false);
                    setAddField(false);
                  }}
                  addDisabled={!viewField || !canEdit}
                  modClick={() => {
                    setViewField(false);
                    setEditButtonvisible(false);
                  }}
                  deleteClick={() => {
                    setShowDeleteDialog(true);
                  }}
                  deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                  modDisabled={!viewField || operatorsDisabled || !canEdit}
                  copyClick={() => {
                    setViewField(false);
                    setAddField(false);
                  }}
                  copyDisabled={!viewField || operatorsDisabled || !canEdit}
                />
              </div>

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <ServiceOperationFields
                      viewField={viewField}
                      autoCompleteOptions={autoCompleteOptions}
                      selectedValues={selectedValues}
                    />

                    <ServiceOperationTabs
                      disabled={viewField}
                      defaultCheckList={defaultCheckList}
                      defaultMaterial={defaultMaterial}
                      autoCompleteOptions={autoCompleteOptions}
                      autocompleteData={autocompleteData}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <ServiceOperationReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
